import { throttle } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cursorInRightPanelArea, isAsideArea } from 'src/hooks/page/use-dnd/helper';
import { useIsDragging } from 'src/hooks/page/use-dnd/hooks';
import { getMindMapEngine } from 'src/mind-map/utils/mind-engine-utils';
import { cache } from 'src/redux/store';
import { $appUiStateCache } from 'src/services/app';
import { useCursorArea } from 'src/services/app/hook/use-drop-info';
import { getAutoScroll } from 'src/utils/auto-scroll';
import { isMindMap } from 'src/utils/block-type-utils';
import { filterDescendantId } from 'src/utils/block-utils';
import { querySelectorFromMainContent } from 'src/utils/dom';
import { useGetPageId } from 'src/utils/getPageId';

const { autoScroll, raf } = getAutoScroll();

export const DragPreview = () => {
  const isDragging = useIsDragging();
  const cursorArea = useCursorArea();
  const mouseDownPos = useRef<undefined | { x: number; y: number }>();
  const mouseMovePos = useRef<undefined | { x: number; y: number }>();
  const [{ moveX, moveY }, setMoveXY] = useState({ moveX: 0, moveY: 0 });
  const pageId = useGetPageId();

  const isRight = cursorInRightPanelArea(cursorArea);

  useEffect(() => {
    const mousedown = (event: MouseEvent) => {
      mouseDownPos.current = { x: event.clientX, y: event.clientY };
    };

    const mouseup = () => {
      setMoveXY({ moveX: 0, moveY: 0 });
      mouseDownPos.current = undefined;
    };

    document.addEventListener('mousedown', mousedown);
    document.addEventListener('mouseup', mouseup);

    return () => {
      document.removeEventListener('mousedown', mousedown);
      document.removeEventListener('mouseup', mouseup);
    };
  }, []);

  useEffect(() => {
    if (!cursorArea) {
      cancelAnimationFrame(raf.id);
      return;
    }

    const mousemove = throttle((event: MouseEvent) => {
      if (cache.ui.selectedBlocks.length && mouseDownPos.current) {
        const mouseMoveX = event.x - mouseDownPos.current.x;
        const mouseMoveY = event.y - mouseDownPos.current.y;
        mouseMovePos.current = { x: event.x, y: event.y };
        setMoveXY({
          moveX: mouseMoveX,
          moveY: mouseMoveY,
        });
      }
      const container =
        querySelectorFromMainContent('.next-space-page', isRight) ?? document.documentElement;
      if (!container) return;
      if (!isAsideArea(cursorArea)) {
        autoScroll(event.x, event.y, container, 40);
      }
    }, 10);

    document.addEventListener('mousemove', mousemove);
    document.addEventListener('dragover', mousemove);

    return () => {
      document.removeEventListener('mousemove', mousemove);
      document.removeEventListener('dragover', mousemove);
    };
  }, [cursorArea, isRight]);

  const previewHtml = useMemo(() => {
    let html = '';

    if (!isDragging) return;
    if (!cache.ui.selectedBlocks.length) return;
    const filterSelectedBlocks = filterDescendantId(cache.ui.selectedBlocks);
    // 脑图选中超过2个块,特殊preview样式
    if (isMindMap(cache.blocks[pageId]?.type) && filterSelectedBlocks.length > 1) {
      const blockId = $appUiStateCache.$focusBlockId ?? filterSelectedBlocks[0]?.blockId;
      if (!blockId) return;
      const selectors = `.next-space-page [data-block-id="${blockId}"]`;
      const node = querySelectorFromMainContent(selectors, isRight);
      if (!node) return;
      const clonedNode = node.cloneNode(true) as HTMLElement;
      let scaleFactor = 1;
      const rect = node.getBoundingClientRect();
      clonedNode.style.position = 'absolute';
      clonedNode.style.left = `${rect.left}px`;
      clonedNode.style.top = `${rect.top}px`;
      if (node instanceof HTMLElement) {
        const engine = getMindMapEngine(node.dataset.mindMapId ?? '');
        if (engine) {
          scaleFactor = engine.getState().scale * 0.01;
          const scaleWidth = rect.width / scaleFactor;
          const scaleHeight = rect.height / scaleFactor;
          clonedNode.style.transformOrigin = 'left top';
          clonedNode.style.scale = scaleFactor.toString();
          clonedNode.style.borderColor = 'rgba(0, 0, 0, 0.1)';
          clonedNode.style.width = `${scaleWidth}px`;
          clonedNode.style.height = `${scaleHeight}px`;
        }
      }
      const clonedNodeOther = clonedNode.cloneNode(true) as HTMLElement;
      clonedNodeOther.style.left = `${rect.left + 10 * scaleFactor}px`;
      clonedNodeOther.style.top = `${rect.top + 10 * scaleFactor}px`;

      html += clonedNodeOther.outerHTML.replace(
        /(block-background.*?(?=")|pointer-events-auto)/,
        ''
      );
      html += clonedNode.outerHTML.replace(/(block-background.*?(?=")|pointer-events-auto)/, '');
      return html;
    }

    filterSelectedBlocks.forEach((item) => {
      const blockNodes: Element[] = [];

      if (item.groupValues) {
        Object.keys(item.groupValues).forEach((groupValue) => {
          let selectors = `.next-space-page [data-block-id="${item.blockId}"][data-view-id="${item.viewId}"]`;
          if (groupValue) {
            selectors += `[data-group-value="${groupValue}"]`;
          }

          const subGroupValues = item.groupValues?.[groupValue] ?? [];
          if (subGroupValues.length > 0) {
            subGroupValues.forEach((subGroupValue) => {
              selectors += `[data-sub-group-value="${subGroupValue}"]`;
              const node = querySelectorFromMainContent(selectors, isRight);
              if (node) blockNodes.push(node);
            });
          } else {
            const node = querySelectorFromMainContent(selectors, isRight);
            if (node) blockNodes.push(node);
          }
        });
      } else {
        let selectors = `.next-space-page [data-block-id="${item.blockId}"]`;
        if (item.viewId) {
          selectors += `[data-view-id="${item.viewId}"]`;
        }
        const node = querySelectorFromMainContent(selectors, isRight);
        if (node) blockNodes.push(node);
      }

      blockNodes.forEach((node) => {
        const clonedNode = node.cloneNode(true) as HTMLElement;
        let scaleFactor = 1;
        const rect = node.getBoundingClientRect();
        clonedNode.style.position = 'absolute';
        clonedNode.style.width = `${rect.width}px`;
        clonedNode.style.height = `${rect.height}px`;
        clonedNode.style.left = `${rect.left}px`;
        clonedNode.style.top = `${rect.top}px`;
        if (node instanceof HTMLElement) {
          const engine = getMindMapEngine(node.dataset.mindMapId ?? '');
          if (engine) {
            scaleFactor = engine.getState().scale * 0.01;
            const scaleWidth = rect.width / scaleFactor;
            const scaleHeight = rect.height / scaleFactor;
            clonedNode.style.transformOrigin = 'left top';
            clonedNode.style.scale = scaleFactor.toString();
            clonedNode.style.borderColor = 'rgba(0, 0, 0, 0.1)';
            clonedNode.style.width = `${scaleWidth}px`;
            clonedNode.style.height = `${scaleHeight}px`;
          }
        }

        html += clonedNode.outerHTML.replace(/(block-background.*?(?=")|pointer-events-auto)/, '');
      });
    });

    return html;

    // 不能依赖 isRight, 会导致拖拽的阴影消失
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, isRight]);

  if (!cursorArea || !isDragging || !previewHtml) return null;

  if (moveX === 0 && moveY === 0) return null;
  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100%',
        width: '100%',
        pointerEvents: 'none', // 必须设置这个才能被正确 drop, ReactDND 依赖 hover 事件s
        zIndex: 999,
        opacity: 0.5,
        transform: `translate3d(${moveX}px,${moveY}px, 0)`,
        color: 'var(--black)',
      }}
      dangerouslySetInnerHTML={{ __html: previewHtml }}
    />,
    document.body
  );
};
